import { serverLogger } from './logger';
import { classifyRefreshAttempt, type RefreshOutcome } from './refresh-outcome';

export type { RefreshOutcome };
import { getRequestContext } from './request-context';

/**
 * Token Refresh Manager - Coordinates token refresh attempts across the application
 * Prevents race conditions when multiple components/interceptors try to refresh simultaneously
 *
 * SECURITY: Each user gets their own manager instance to prevent one user's failures
 * from affecting another user's refresh attempts.
 */

class TokenRefreshManager {
  private refreshPromise: Promise<boolean> | null = null;
  private lastSuccessTime: Date | null = null;
  private lastFailureTime: Date | null = null;
  private consecutiveFailures = 0;
  private totalAttempts = 0;
  private totalSuccesses = 0;
  private totalFailures = 0;
  private recoveryTimeout: NodeJS.Timeout | null = null;
  private readonly MIN_INTERVAL_AFTER_SUCCESS = 30000; // 30 seconds after successful refresh
  private readonly MIN_INTERVAL_AFTER_FAILURE = 5000; // 5 seconds minimum between failures
  private readonly MAX_CONSECUTIVE_FAILURES = 3; // Stop after 3 consecutive failures
  private readonly BACKOFF_MULTIPLIER = 2; // Exponential backoff multiplier

  /**
   * Execute a refresh operation, ensuring only one runs at a time.
   *
   * Boolean contract, unchanged: callers that simply retry on failure (dedupFetch, http-client)
   * want exactly this. Callers that decide whether to END THE SESSION must use
   * `executeRefreshDetailed` instead — `false` here conflates "the refresh token was rejected"
   * with "we are backing off" and "the refresh call itself failed", and logging someone out for
   * the latter two is how a throttled refresh or a 502 turns into a surprise trip to /login.
   */
  async executeRefresh(refreshFunction: () => Promise<boolean>): Promise<boolean> {
    return (await this.executeRefreshDetailed(refreshFunction)) === 'success';
  }

  /**
   * The same operation, reporting WHY it did not succeed.
   *
   * - `success`      — a fresh token is in place (or one recent enough to still be valid).
   * - `retry-later`  — nothing is wrong with the session: we are inside the failure backoff, too
   *                    soon after a previous attempt, or the refresh call itself threw (network,
   *                    5xx). The caller should surface a retryable error and leave auth alone.
   * - `rejected`     — the refresh function reported that the credentials are no longer good.
   *                    Only this outcome justifies expiring the session.
   */
  async executeRefreshDetailed(refreshFunction: () => Promise<boolean>): Promise<RefreshOutcome> {
    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      serverLogger.debug('[TokenRefreshManager] Refresh in progress, waiting', this.getRefreshStats());
      // Piggy-backing on an in-flight attempt: its boolean cannot tell rejection from a transport
      // failure, so treat a non-success as retryable rather than ending someone else's session.
      return (await this.refreshPromise) ? 'success' : 'retry-later';
    }

    // Check if we've hit max consecutive failures
    if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
      const timeSinceLastFailure = this.lastFailureTime
        ? Date.now() - this.lastFailureTime.getTime()
        : Infinity;
      const backoffTime =
        this.MIN_INTERVAL_AFTER_FAILURE * Math.pow(this.BACKOFF_MULTIPLIER, this.consecutiveFailures - 1);

      if (timeSinceLastFailure < backoffTime) {
        serverLogger.warn(
          `[TokenRefreshManager] Too many failures (${
            this.consecutiveFailures
          }), backing off for ${Math.round((backoffTime - timeSinceLastFailure) / 1000)}s`,
        );
        return 'retry-later';
      }
      // Reset if enough time has passed
      if (timeSinceLastFailure > 60000) {
        // 1 minute
        serverLogger.debug('[TokenRefreshManager] Resetting failure count after cooldown');
        this.consecutiveFailures = 0;
      }
    }

    // Prevent rapid refreshes after a successful one
    if (this.lastSuccessTime) {
      const timeSinceSuccess = Date.now() - this.lastSuccessTime.getTime();
      if (timeSinceSuccess < this.MIN_INTERVAL_AFTER_SUCCESS) {
        serverLogger.debug('[TokenRefreshManager] Recently refreshed, skipping');
        return 'success'; // the token from that refresh should still be valid
      }
    }

    // Prevent rapid refreshes after a failure
    if (this.lastFailureTime) {
      const timeSinceFailure = Date.now() - this.lastFailureTime.getTime();
      const minInterval =
        this.MIN_INTERVAL_AFTER_FAILURE *
        Math.pow(this.BACKOFF_MULTIPLIER, Math.min(this.consecutiveFailures, 3));
      if (timeSinceFailure < minInterval) {
        serverLogger.debug(
          `[TokenRefreshManager] Too soon after failure, waiting ${Math.round(
            (minInterval - timeSinceFailure) / 1000,
          )}s`,
        );
        return 'retry-later';
      }
    }

    // Create a new refresh promise
    this.totalAttempts++;
    serverLogger.log('[TokenRefreshManager] Token refresh attempt', this.getRefreshStats());
    this.refreshPromise = this.doRefresh(refreshFunction);

    try {
      const result = await this.refreshPromise;
      if (result) {
        this.lastSuccessTime = new Date();
        this.consecutiveFailures = 0; // Reset on success
        this.lastFailureTime = null;
        this.totalSuccesses++;
        serverLogger.log('[TokenRefreshManager] Token refresh succeeded', this.getRefreshStats());
        return 'success';
      }
      this.lastFailureTime = new Date();
      this.consecutiveFailures++;
      this.totalFailures++;
      serverLogger.warn('[TokenRefreshManager] Token refresh failed', this.getRefreshStats());
      // `threw` means the attempt never got an answer — a network drop or a 5xx on the refresh
      // endpoint. That says nothing about whether the credentials are still good.
      return classifyRefreshAttempt({ ok: false, threw: this.lastAttemptThrew });
    } finally {
      this.refreshPromise = null;
    }
  }

  /** Whether the most recent `doRefresh` failed by throwing rather than by reporting a rejection. */
  private lastAttemptThrew = false;

  private async doRefresh(refreshFunction: () => Promise<boolean>): Promise<boolean> {
    this.lastAttemptThrew = false;
    try {
      serverLogger.debug('[TokenRefreshManager] Starting token refresh');
      const result = await refreshFunction();
      serverLogger.debug('[TokenRefreshManager] Token refresh completed', { result });
      return result;
    } catch (error) {
      serverLogger.error('[TokenRefreshManager] Token refresh failed', { error });
      this.lastAttemptThrew = true;
      return false;
    }
  }

  /**
   * Check if a refresh is currently in progress
   */
  isRefreshing(): boolean {
    return this.refreshPromise !== null;
  }

  /**
   * Get current failure count
   */
  getFailureCount(): number {
    return this.consecutiveFailures;
  }

  /**
   * Get aggregate refresh statistics
   */
  getRefreshStats(): { attempts: number; successes: number; failures: number; consecutiveFailures: number } {
    return {
      attempts: this.totalAttempts,
      successes: this.totalSuccesses,
      failures: this.totalFailures,
      consecutiveFailures: this.consecutiveFailures,
    };
  }

  /**
   * Check if refresh is blocked due to failures
   */
  isBlocked(): boolean {
    if (this.consecutiveFailures >= this.MAX_CONSECUTIVE_FAILURES) {
      const timeSinceLastFailure = this.lastFailureTime
        ? Date.now() - this.lastFailureTime.getTime()
        : Infinity;
      const backoffTime =
        this.MIN_INTERVAL_AFTER_FAILURE * Math.pow(this.BACKOFF_MULTIPLIER, this.consecutiveFailures - 1);
      return timeSinceLastFailure < backoffTime;
    }
    return false;
  }

  /**
   * Reset the manager state (useful for logout)
   */
  reset(): void {
    this.refreshPromise = null;
    this.lastSuccessTime = null;
    this.lastFailureTime = null;
    this.consecutiveFailures = 0;
    this.totalAttempts = 0;
    this.totalSuccesses = 0;
    this.totalFailures = 0;
    if (this.recoveryTimeout) {
      clearTimeout(this.recoveryTimeout);
      this.recoveryTimeout = null;
    }
  }

  /**
   * Schedule automatic recovery after backoff period
   */
  scheduleRecovery(): void {
    if (this.recoveryTimeout) return;

    const backoffTime = this.getBackoffTime();
    serverLogger.log('[TokenRefreshManager] Scheduling recovery', { backoffTime });

    this.recoveryTimeout = setTimeout(() => {
      serverLogger.log('[TokenRefreshManager] Auto-recovery triggered');
      this.reset(); // Reset failure counts
      this.recoveryTimeout = null;

      // Trigger recovery event for UI components
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('token-recovery-ready'));
      }
    }, backoffTime);
  }

  /**
   * Get current backoff time in milliseconds
   */
  private getBackoffTime(): number {
    return (
      this.MIN_INTERVAL_AFTER_FAILURE *
      Math.pow(this.BACKOFF_MULTIPLIER, Math.min(this.consecutiveFailures, 4))
    );
  }

  /**
   * Force immediate recovery (used for manual retry)
   */
  async forceRecovery(refreshFunction?: () => Promise<boolean>): Promise<boolean> {
    serverLogger.log('[TokenRefreshManager] Force recovery initiated');

    // Clear recovery timeout if scheduled
    if (this.recoveryTimeout) {
      clearTimeout(this.recoveryTimeout);
      this.recoveryTimeout = null;
    }

    // Reset failure state
    this.consecutiveFailures = 0;
    this.lastFailureTime = null;

    // If refresh function provided, execute it
    if (refreshFunction) {
      return this.executeRefresh(refreshFunction);
    }

    return true;
  }
}

/**
 * Manager entry with metadata for cleanup
 */
interface ManagerEntry {
  instance: TokenRefreshManager;
  lastAccess: number;
  userId?: string;
  tenantId?: string;
}

/**
 * Token Refresh Manager Factory
 *
 * SECURITY: Provides isolated manager instances per user to prevent:
 * - Cross-user promise sharing (DoS risk)
 * - Shared failure counters (one user's failures blocking another)
 * - Shared backoff timers (timing leaks)
 *
 * CLIENT-SIDE: Uses a singleton manager (safe, only one user per browser)
 * SERVER-SIDE: Creates per-user managers with automatic cleanup
 */
class TokenRefreshManagerFactory {
  private managers = new Map<string, ManagerEntry>();
  private clientManager: TokenRefreshManager | null = null;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private readonly MANAGER_TTL = 30 * 60 * 1000; // 30 minutes
  private readonly CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes

  /**
   * Get or create a manager for the current user/request
   *
   * @param userId - Optional user identifier (auto-detected from request context if available)
   * @param tenantId - Optional tenant identifier for additional scoping
   * @returns TokenRefreshManager instance scoped to the user/request
   */
  getManager(userId?: string, tenantId?: string): TokenRefreshManager {
    // CLIENT-SIDE: Use singleton (safe, only one user per browser)
    if (typeof window !== 'undefined') {
      if (!this.clientManager) {
        this.clientManager = new TokenRefreshManager();
        serverLogger.debug('[TokenRefreshManagerFactory] Created client-side singleton manager');
      }
      return this.clientManager;
    }

    // SERVER-SIDE: Try to get user from request context first
    const context = getRequestContext();
    const contextUserId = context?.userId || userId;
    const contextTenantId = context?.tenantId || tenantId;

    // Create unique key for this user/tenant combination
    const key = this.createKey(contextUserId, contextTenantId);

    // Get or create manager for this user
    if (!this.managers.has(key)) {
      const manager = new TokenRefreshManager();
      this.managers.set(key, {
        instance: manager,
        lastAccess: Date.now(),
        userId: contextUserId,
        tenantId: contextTenantId,
      });

      serverLogger.debug('[TokenRefreshManagerFactory] Created manager', {
        key,
        userId: contextUserId,
        tenantId: contextTenantId,
        totalManagers: this.managers.size,
      });

      // Ensure cleanup is running
      this.ensureCleanup();
    } else {
      // Update last access time
      const entry = this.managers.get(key)!;
      entry.lastAccess = Date.now();
    }

    return this.managers.get(key)!.instance;
  }

  /**
   * Create a unique key for manager lookup
   * Falls back to temporary key if no user identifier available
   */
  private createKey(userId?: string, tenantId?: string): string {
    if (!userId) {
      // No userId - create a temporary key (will be cleaned up after TTL)
      // This should be rare on server-side
      const tempKey = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      serverLogger.warn('[TokenRefreshManagerFactory] No userId provided, using temporary key', { tempKey });
      return tempKey;
    }

    return tenantId ? `${userId}:${tenantId}` : userId;
  }

  /**
   * Reset manager for a specific user (useful for logout)
   */
  resetManager(userId?: string, tenantId?: string): void {
    // Client-side: reset singleton
    if (typeof window !== 'undefined') {
      this.clientManager?.reset();
      return;
    }

    // Server-side: reset specific user's manager
    const context = getRequestContext();
    const contextUserId = context?.userId || userId;
    const contextTenantId = context?.tenantId || tenantId;
    const key = this.createKey(contextUserId, contextTenantId);

    const entry = this.managers.get(key);
    if (entry) {
      entry.instance.reset();
      serverLogger.debug('[TokenRefreshManagerFactory] Reset manager', { key });
    }
  }

  /**
   * Clean up stale managers to prevent memory leaks
   */
  private cleanup(): void {
    const now = Date.now();
    let cleaned = 0;

    for (const [key, entry] of this.managers.entries()) {
      if (now - entry.lastAccess > this.MANAGER_TTL) {
        this.managers.delete(key);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      serverLogger.log('[TokenRefreshManagerFactory] Cleaned up stale managers', {
        cleaned,
        remaining: this.managers.size,
      });
    }
  }

  /**
   * Ensure cleanup interval is running (server-side only)
   */
  private ensureCleanup(): void {
    if (this.cleanupInterval || typeof window !== 'undefined') return;

    this.cleanupInterval = setInterval(() => this.cleanup(), this.CLEANUP_INTERVAL);

    // Don't keep Node.js process alive just for cleanup
    if (this.cleanupInterval.unref) {
      this.cleanupInterval.unref();
    }

    serverLogger.debug('[TokenRefreshManagerFactory] Started cleanup interval');
  }

  /**
   * Get factory statistics for monitoring
   */
  getStats(): {
    activeManagers: number;
    managers: Array<{
      key: string;
      userId?: string;
      tenantId?: string;
      lastAccess: Date;
      stats: ReturnType<TokenRefreshManager['getRefreshStats']>;
    }>;
  } {
    return {
      activeManagers: this.managers.size,
      managers: Array.from(this.managers.entries()).map(([key, entry]) => ({
        key,
        userId: entry.userId,
        tenantId: entry.tenantId,
        lastAccess: new Date(entry.lastAccess),
        stats: entry.instance.getRefreshStats(),
      })),
    };
  }

  /**
   * Stop cleanup interval (useful for testing)
   */
  stopCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }
}

// Export factory instance
export const tokenRefreshManagerFactory = new TokenRefreshManagerFactory();

/**
 * Get token refresh manager for current user/request
 *
 * PREFERRED API: Use this function instead of the deprecated singleton
 *
 * @param userId - Optional user identifier (auto-detected from request context)
 * @param tenantId - Optional tenant identifier
 * @returns TokenRefreshManager instance scoped to the user
 */
export function getTokenRefreshManager(userId?: string, tenantId?: string): TokenRefreshManager {
  return tokenRefreshManagerFactory.getManager(userId, tenantId);
}

/**
 * @deprecated Use getTokenRefreshManager() instead
 *
 * This singleton is maintained for backward compatibility but will be removed in a future version.
 * It now delegates to the factory to provide per-user isolation.
 */
export const tokenRefreshManager = {
  executeRefresh: async (refreshFunction: () => Promise<boolean>) => {
    const manager = getTokenRefreshManager();
    return manager.executeRefresh(refreshFunction);
  },
  isRefreshing: () => {
    const manager = getTokenRefreshManager();
    return manager.isRefreshing();
  },
  getFailureCount: () => {
    const manager = getTokenRefreshManager();
    return manager.getFailureCount();
  },
  getRefreshStats: () => {
    const manager = getTokenRefreshManager();
    return manager.getRefreshStats();
  },
  isBlocked: () => {
    const manager = getTokenRefreshManager();
    return manager.isBlocked();
  },
  reset: () => {
    const manager = getTokenRefreshManager();
    return manager.reset();
  },
  scheduleRecovery: () => {
    const manager = getTokenRefreshManager();
    return manager.scheduleRecovery();
  },
  forceRecovery: async (refreshFunction?: () => Promise<boolean>) => {
    const manager = getTokenRefreshManager();
    return manager.forceRecovery(refreshFunction);
  },
};
